import React, { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import styled from 'styled-components';
import { FormGrid } from '@packages/ui/patterns';
import type { FormEachNode } from '../../../types/layout';
import type { FormFieldMapping } from '../../../types/fieldMapping';
import { FormFieldAdapter } from '../FormFieldAdapter';
import { useFormEngineForm, useFormEngineState } from '../../../context/FormEngineContext';
import { FormRowProvider } from '../../../context/FormRowContext';
import { useEffectProcessor } from '../../../processors/effectProcessor';
import { useObserverProcessor } from '../../../processors/observerProcessor';
import { useRowObserverProcessor } from '../../../processors/rowObserverProcessor';
import { prefixEffects, prefixObservers } from '../../../processors/pathRewriter';
import { resolveItemTokens } from '../../../resolver/tokenResolver';
import { applyTitleFrom } from './applyTitleFrom';
import { mergeMatrixFieldShellPresentation } from '../../../utils/shellPresentation';

interface FormEachItemFieldsProps {
  node: FormEachNode;
  index: number;
  total: number;
  columns: number | string | Record<string, number | string>;
  children?: React.ReactNode;
  itemClassName?: string;
}

/** Legacy account row — full-width 3-column grid, no extra chrome. */
const ViewAccountRow = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $columns }) => $columns}, minmax(0, 1fr));
  width: 100%;
  gap: 0;
  row-gap: ${8}px;
`;

export const FormEachItemFields = React.memo(function FormEachItemFields({
  node,
  index,
  total,
  columns,
  children,
  itemClassName,
}: FormEachItemFieldsProps): React.ReactElement {
  const { form, programmaticWriteGuard } = useFormEngineForm();
  const { meta, getOptionProp, isReadOnly } = useFormEngineState();

  const namePrefix = `${node.name}.${index}`;
  const rowValues = useWatch({ control: form.control, name: namePrefix }) as
    | Record<string, unknown>
    | undefined;

  const rowMeta = useMemo(
    () => ({
      index,
      total,
      isFirst: index === 0,
      isLast: index === total - 1,
      documentCategory: node.documentCategory,
    }),
    [index, total, node.documentCategory],
  );

  const resolvedItemFields = useMemo(() => {
    return node.itemFields.map((field) =>
      applyTitleFrom(resolveItemTokens(field, namePrefix) as FormFieldMapping, rowValues),
    );
  }, [node.itemFields, namePrefix, rowValues]);

  const scopedEffects = useMemo(
    () => (node.itemEffects?.length ? prefixEffects(node.itemEffects, namePrefix) : []),
    [node.itemEffects, namePrefix],
  );

  const scopedObservers = useMemo(
    () => (node.itemObservers?.length ? prefixObservers(node.itemObservers, namePrefix) : []),
    [node.itemObservers, namePrefix],
  );

  useEffectProcessor({ form: form as never, effects: scopedEffects, meta, getOptionProp, programmaticWriteGuard });
  useObserverProcessor({ form: form as never, observers: scopedObservers, meta, getOptionProp });
  useRowObserverProcessor({
    form: form as never,
    observers: node.itemRowObservers ?? [],
    rowPrefix: namePrefix,
    meta,
    getOptionProp,
  });

  const gridColumns = typeof columns === 'number' ? columns : 3;

  return (
    <FormRowProvider value={rowMeta}>
      <div
        className={itemClassName}
        data-form-each-item={index}
        data-document-category={node.documentCategory ?? undefined}>
        {isReadOnly && typeof columns === 'number' ? (
          <ViewAccountRow $columns={gridColumns}>
            {resolvedItemFields.map((field, fieldIndex) => {
              const { style: shellStyle, className: shellClassName } = mergeMatrixFieldShellPresentation({
                shellPresentation: field.shellPresentation,
              });
              return (
              <div key={field.name || `__each_row_${fieldIndex}`} className={shellClassName} style={shellStyle}>
                <FormFieldAdapter field={field} namePrefix={namePrefix} />
              </div>
              );
            })}
            {children}
          </ViewAccountRow>
        ) : (
          <FormGrid columns={columns}>
            {resolvedItemFields.map((field, fieldIndex) => {
              const { style: shellStyle, className: shellClassName } = mergeMatrixFieldShellPresentation({
                shellPresentation: field.shellPresentation,
              });
              return (
              <FormGrid.Item key={field.name || `__each_row_${fieldIndex}`} colSpan={field.colSpan}>
                <div className={shellClassName} style={shellStyle}>
                  <FormFieldAdapter field={field} namePrefix={namePrefix} />
                </div>
              </FormGrid.Item>
              );
            })}
            {children}
          </FormGrid>
        )}
      </div>
    </FormRowProvider>
  );
});
